import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import BrandButton from "../common/BrandButton/BrandButton";
import { Invoice, IssueInvoiceInput } from "./types/invoice";
import { useIssueInvoice } from "../../hooks/api/useInvoice";
import { useReportError } from "../../hooks/useReportError";
import { OrderStatus } from "../Orders/enums/orderStatus";
import type { Order } from "../Orders/types/salesOrder";
import { canIssueInvoice as canIssueInvoiceUtil } from "../Orders/utils/orderUtils";

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderStatus: OrderStatus;
  /** Pass the full order so the modal can guard against zero-item / zero-price orders. */
  order?: Order | null;
  /** Called after a successful issue; e.g. navigate to invoice detail. */
  onInvoiceIssued?: (invoice: Invoice) => void;
}

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open,
  onClose,
  orderId,
  orderStatus,
  order,
  onInvoiceIssued
}) => {
  const issueInvoice = useIssueInvoice();
  const reportError = useReportError();
  const { t } = useKivunovaTranslation();

  // No existing invoice here (modal is only opened when there isn't one).
  // The third argument enforces the items guard.
  const canIssue = canIssueInvoiceUtil(null, orderStatus, order);

  // Derive a human-readable reason so the warning copy is accurate.
  const warningReason: string | null = (() => {
    if (orderStatus !== OrderStatus.OFFER_ACCEPTED) {
      return t(
        "invoice-status-warning",
        "Invoice can only be issued when the order is confirmed (OFFER_ACCEPTED) and has no invoice yet."
      );
    }
    if (
      !order ||
      !Array.isArray(order.items) ||
      order.items.length === 0 ||
      !order.items.some((item) => item.quantity > 0 && item.unitPrice > 0)
    ) {
      return t(
        "invoice-no-items-warning",
        "Invoice cannot be issued because this order has no items with a valid quantity and price."
      );
    }
    return null;
  })();

  const handleConfirm = async () => {
    if (!canIssue) return;
    const issueDate = new Date().toISOString().split("T")[0];
    const input: IssueInvoiceInput = { orderId, issueDate };
    try {
      const invoice = await issueInvoice.mutateAsync(input);
      if (invoice) {
        onInvoiceIssued?.(invoice);
      }
      onClose();
    } catch (error) {
      reportError("Failed to issue invoice (modal)", error, {
        component: "CreateInvoiceModal",
        orderId
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1
        }}
      >
        <Typography component="span" variant="h6" fontWeight="bold">
          {t("issue-invoice", "Issue Invoice")}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={t("close", "Close")}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("for-order", "For Order")}:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {orderId}
          </Box>
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t(
            "issue-invoice-confirm-body",
            "Create an invoice for this order using today's date as the issue date?"
          )}
        </Typography>
        {warningReason && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "warning.light",
              borderRadius: 1
            }}
          >
            <Typography variant="body2" color="warning.dark">
              {warningReason}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "flex-end"
        }}
      >
        <BrandButton
          variant="outlined"
          onClick={onClose}
          disabled={issueInvoice.isPending}
        >
          {t("cancel", "Cancel")}
        </BrandButton>
        <LoadingButton
          variant="contained"
          disableElevation
          loading={issueInvoice.isPending}
          disabled={!canIssue}
          onClick={() => void handleConfirm()}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.02em",
            px: 2,
            py: 1,
            backgroundColor: BRAND_COLOR,
            color: "white",
            boxShadow: `0 2px 8px ${BRAND_COLOR}40`,
            "&:hover": {
              backgroundColor: BRAND_HOVER,
              boxShadow: `0 4px 12px ${BRAND_COLOR}50`
            }
          }}
        >
          {t("issue-invoice", "Issue Invoice")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoiceModal;
